import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Grid, Card,
  Button, Box, TextField, Chip, CircularProgress, IconButton,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import FavoriteIcon      from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StarIcon          from '@mui/icons-material/Star';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalPizzaIcon    from '@mui/icons-material/LocalPizza';
import LunchDiningIcon   from '@mui/icons-material/LunchDining';
import SetMealIcon       from '@mui/icons-material/SetMeal';
import CakeIcon          from '@mui/icons-material/Cake';
import RiceBowlIcon      from '@mui/icons-material/RiceBowl';
import KebabDiningIcon   from '@mui/icons-material/KebabDining';
import BakeryDiningIcon  from '@mui/icons-material/BakeryDining';
import SoupKitchenIcon   from '@mui/icons-material/SoupKitchen';
import RamenDiningIcon   from '@mui/icons-material/RamenDining';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import SpaIcon           from '@mui/icons-material/Spa';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import restaurantService from '../../services/restaurant.service';

/* ── Palette ── */
const C = {
  purple:     '#552583',
  purpleDark: '#33154f',
  gold:       '#FDB927',
  bg:         '#f6f6f6',
  white:      '#ffffff',
  ink:        '#1a1a1a',
  soft:       '#4a4a4a',
  muted:      '#8a8a8a',
  border:     '#ebebeb',
};

const categoryIcons = {
  'Pizza':      { icon: LocalPizzaIcon,    color: '#FF6B6B' },
  'Poulet':     { icon: KebabDiningIcon,   color: '#552583' },
  'Burger':     { icon: LunchDiningIcon,   color: '#FDB927' },
  'Grillades':  { icon: KebabDiningIcon,   color: '#8B4513' },
  'Poisson':    { icon: SetMealIcon,       color: '#4682B4' },
  'Pâtes':      { icon: RamenDiningIcon,   color: '#FF8C00' },
  'Sandwiches': { icon: BakeryDiningIcon,  color: '#DAA520' },
  'Desserts':   { icon: CakeIcon,          color: '#FF69B4' },
  'Africain':   { icon: SoupKitchenIcon,   color: '#D2691E' },
  'Européen':   { icon: RiceBowlIcon,      color: '#4169E1' },
  'Asiatique':  { icon: RamenDiningIcon,   color: '#DC143C' },
  'Fast-food':  { icon: TakeoutDiningIcon, color: '#FF4500' },
  'Grill':      { icon: KebabDiningIcon,   color: '#8B4513' },
  'Végétarien': { icon: SpaIcon,           color: '#32CD32' },
};

const fmt = (n) =>
  Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RestaurantHome = () => {
  const [restaurants,         setRestaurants]         = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [keyword,             setKeyword]             = useState('');
  const [favorites,           setFavorites]           = useState([]);
  const [categories,          setCategories]          = useState([]);
  const [selectedCategory,    setSelectedCategory]    = useState('');
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [openNowFilter,       setOpenNowFilter]       = useState(false);
  const [freeDeliveryFilter,  setFreeDeliveryFilter]  = useState(false);
  const catScrollRef = useRef(null);

  useEffect(() => { loadRestaurants(); }, []);

  const loadRestaurants = async () => {
    try {
      const data = await restaurantService.getAll();
      const list = data.restaurants || [];
      setRestaurants(list);
      setFilteredRestaurants(list);
      const count = {};
      list.forEach(r => { const c = r.cuisine_type || 'Divers'; count[c] = (count[c] || 0) + 1; });
      setCategories(
        Object.keys(count)
          .map(name => ({
            name,
            count: count[name],
            IconComp: categoryIcons[name]?.icon || RestaurantMenuIcon,
            color: categoryIcons[name]?.color || '#808080',
          }))
          .sort((a, b) => b.count - a.count)
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (list, {
    kw       = keyword,
    cat      = selectedCategory,
    openOnly = openNowFilter,
    freeOnly = freeDeliveryFilter,
  } = {}) =>
    list.filter(r =>
      (!kw       || r.name?.toLowerCase().includes(kw.toLowerCase()) || r.cuisine_type?.toLowerCase().includes(kw.toLowerCase())) &&
      (!cat      || r.cuisine_type === cat) &&
      (!openOnly || r.is_open) &&
      (!freeOnly || r.delivery_fee === 0)
    );

  const handleSearch = (kw = keyword) => {
    setFilteredRestaurants(applyFilters(restaurants, { kw }));
  };

  const handleCategoryClick = (name) => {
    const next = selectedCategory === name ? '' : name;
    setSelectedCategory(next);
    setFilteredRestaurants(applyFilters(restaurants, { cat: next }));
  };

  const toggleOpenNow = () => {
    const next = !openNowFilter;
    setOpenNowFilter(next);
    setFilteredRestaurants(applyFilters(restaurants, { openOnly: next }));
  };

  const toggleFreeDelivery = () => {
    const next = !freeDeliveryFilter;
    setFreeDeliveryFilter(next);
    setFilteredRestaurants(applyFilters(restaurants, { freeOnly: next }));
  };

  const resetFilters = () => {
    setKeyword(''); setSelectedCategory(''); setOpenNowFilter(false); setFreeDeliveryFilter(false);
    setFilteredRestaurants(restaurants);
  };

  const toggleFavorite = (id) =>
    setFavorites(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: C.purple }} />
      </Box>
    );
  }

  const hasActiveFilters = selectedCategory || openNowFilter || freeDeliveryFilter || keyword;

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>

      {/* ── STICKY TOP BAR : search + categories + filters ── */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 100,
        bgcolor: C.white,
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* Search row */}
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 1.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            maxWidth: 640,
          }}>
            <Box sx={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: C.bg, borderRadius: '12px', px: 2, py: 1,
              border: `1.5px solid ${C.border}`,
              '&:focus-within': { borderColor: C.purple },
              transition: 'border-color 0.2s',
            }}>
              <SearchIcon sx={{ color: C.muted, fontSize: 20, flexShrink: 0 }} />
              <TextField
                fullWidth
                placeholder="Rechercher un restaurant ou un plat..."
                value={keyword}
                onChange={e => { setKeyword(e.target.value); handleSearch(e.target.value); }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '0.9rem', color: C.ink, '& input::placeholder': { color: C.muted, opacity: 1 } },
                }}
              />
              {keyword && (
                <IconButton size="small" onClick={() => { setKeyword(''); handleSearch(''); }}
                  sx={{ p: 0.3, color: C.muted }}>
                  ✕
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Categories + filter chips */}
        <Box
          ref={catScrollRef}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: { xs: 2, md: 4 }, pb: 1.5,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {/* "Tous" pill */}
          <Box
            onClick={resetFilters}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.8,
              px: 2, py: 0.8, borderRadius: '20px', cursor: 'pointer', flexShrink: 0,
              bgcolor: !hasActiveFilters ? C.purple : 'transparent',
              color: !hasActiveFilters ? 'white' : C.soft,
              border: `1.5px solid ${!hasActiveFilters ? C.purple : C.border}`,
              fontSize: '0.85rem', fontWeight: 600,
              transition: 'all 0.2s',
            }}
          >
            Tous
          </Box>

          {/* Category pills */}
          {categories.map((cat) => {
            const active = selectedCategory === cat.name;
            return (
              <Box
                key={cat.name}
                onClick={() => handleCategoryClick(cat.name)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8,
                  px: 2, py: 0.8, borderRadius: '20px', cursor: 'pointer', flexShrink: 0,
                  bgcolor: active ? C.purple : 'transparent',
                  color: active ? 'white' : C.soft,
                  border: `1.5px solid ${active ? C.purple : C.border}`,
                  fontSize: '0.85rem', fontWeight: 600,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: C.purple },
                }}
              >
                {React.createElement(cat.IconComp, { sx: { fontSize: '1rem', color: active ? 'white' : cat.color } })}
                <span style={{ whiteSpace: 'nowrap' }}>{cat.name}</span>
              </Box>
            );
          })}

          {/* Separator */}
          <Box sx={{ width: 1, height: 24, bgcolor: C.border, flexShrink: 0, mx: 0.5 }} />

          {/* Filter chips */}
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: openNowFilter ? 'white !important' : `${C.purple} !important` }} />}
            label="Ouvert maintenant"
            clickable onClick={toggleOpenNow}
            size="small"
            sx={{
              flexShrink: 0, fontWeight: 600, fontSize: '0.82rem', borderRadius: '20px',
              bgcolor: openNowFilter ? C.purple : 'transparent',
              border: `1.5px solid ${openNowFilter ? C.purple : C.border}`,
              color: openNowFilter ? 'white' : C.soft,
              height: 34,
              '& .MuiChip-label': { px: 1 },
            }}
          />
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: '14px !important', color: freeDeliveryFilter ? 'white !important' : `${C.purple} !important` }} />}
            label="Livraison gratuite"
            clickable onClick={toggleFreeDelivery}
            size="small"
            sx={{
              flexShrink: 0, fontWeight: 600, fontSize: '0.82rem', borderRadius: '20px',
              bgcolor: freeDeliveryFilter ? C.purple : 'transparent',
              border: `1.5px solid ${freeDeliveryFilter ? C.purple : C.border}`,
              color: freeDeliveryFilter ? 'white' : C.soft,
              height: 34,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>

        {/* Section title */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', md: '1.5rem' }, color: C.ink }}>
            {selectedCategory
              ? selectedCategory
              : openNowFilter
              ? 'Ouverts maintenant'
              : freeDeliveryFilter
              ? 'Livraison gratuite'
              : keyword
              ? `Résultats pour "${keyword}"`
              : 'Tous les restaurants'}
            {' '}
            <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.9rem', color: C.muted }}>
              ({filteredRestaurants.length})
            </Typography>
          </Typography>
          {hasActiveFilters && (
            <Button onClick={resetFilters}
              sx={{ color: C.purple, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                '&:hover': { bgcolor: 'rgba(85,37,131,0.06)' } }}>
              Effacer les filtres
            </Button>
          )}
        </Box>

        {/* Restaurant grid */}
        {filteredRestaurants.length > 0 ? (
          <Grid container spacing={{ xs: 2, md: 2.5 }}>
            {filteredRestaurants.map((r) => (
              <Grid item xs={12} sm={6} md={4} key={r.id}>
                <Card
                  component={Link}
                  to={`/restaurants/${r.id}`}
                  sx={{
                    display: 'flex', flexDirection: 'column',
                    textDecoration: 'none', color: 'inherit',
                    bgcolor: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    opacity: r.is_open ? 1 : 0.65,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Image */}
                  <Box sx={{ position: 'relative', pt: '56%' /* 16:9 */, overflow: 'hidden' }}>
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      backgroundImage: r.cover_image
                        ? `url(${r.cover_image})`
                        : `linear-gradient(135deg, rgba(85,37,131,0.12) 0%, rgba(253,185,39,0.15) 100%)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transition: 'transform 0.3s',
                      '&:hover': { transform: 'scale(1.03)' },
                    }} />

                    {/* Closed overlay */}
                    {!r.is_open && (
                      <Box sx={{
                        position: 'absolute', inset: 0,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Box sx={{
                          bgcolor: 'rgba(0,0,0,0.7)', color: 'white',
                          px: 2, py: 0.6, borderRadius: '8px',
                          fontSize: '0.8rem', fontWeight: 700,
                        }}>
                          Fermé
                        </Box>
                      </Box>
                    )}

                    {/* Free delivery badge */}
                    {r.delivery_fee === 0 && (
                      <Box sx={{
                        position: 'absolute', bottom: 10, left: 10,
                        bgcolor: C.purple, color: 'white',
                        px: 1.2, py: 0.3, borderRadius: '6px',
                        fontSize: '0.7rem', fontWeight: 700,
                      }}>
                        Livraison gratuite
                      </Box>
                    )}

                    {/* Favorite */}
                    <IconButton
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(r.id); }}
                      sx={{
                        position: 'absolute', top: 10, right: 10,
                        bgcolor: 'rgba(255,255,255,0.9)', width: 36, height: 36,
                        backdropFilter: 'blur(4px)',
                        '&:hover': { bgcolor: 'white' },
                      }}
                    >
                      {favorites.includes(r.id)
                        ? <FavoriteIcon sx={{ color: '#e74c3c', fontSize: 18 }} />
                        : <FavoriteBorderIcon sx={{ fontSize: 18, color: C.soft }} />}
                    </IconButton>
                  </Box>

                  {/* Info */}
                  <Box sx={{ p: 2, pb: 2.5 }}>
                    <Typography sx={{
                      fontWeight: 700, fontSize: '1rem', color: C.ink,
                      mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.name}
                    </Typography>

                    {/* Rating + cuisine */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                      <StarIcon sx={{ fontSize: 14, color: C.gold }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.ink }}>
                        {r.rating || '4.5'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: C.muted }}>
                        · {r.cuisine_type || 'Divers'}
                      </Typography>
                    </Box>

                    {/* Time + fee */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 13, color: C.muted }} />
                      <Typography sx={{ fontSize: '0.8rem', color: C.muted }}>
                        {r.avg_delivery_time || 30} min
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: C.muted, mx: 0.3 }}>·</Typography>
                      <Typography sx={{
                        fontSize: '0.8rem', color: r.delivery_fee === 0 ? C.purple : C.muted,
                        fontWeight: r.delivery_fee === 0 ? 700 : 400,
                      }}>
                        {r.delivery_fee === 0
                          ? 'Livraison offerte'
                          : `Livraison ${fmt(r.delivery_fee)} €`}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <RestaurantMenuIcon sx={{ fontSize: 64, color: C.muted, opacity: 0.3, mb: 2 }} />
            <Typography sx={{ color: C.soft, fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
              Aucun restaurant trouvé
            </Typography>
            <Typography sx={{ color: C.muted, mb: 3, fontSize: '0.9rem' }}>
              Essayez de modifier vos filtres
            </Typography>
            <Button onClick={resetFilters}
              sx={{
                bgcolor: C.purple, color: 'white', fontWeight: 700,
                borderRadius: '12px', px: 4, py: 1.2, textTransform: 'none',
                '&:hover': { bgcolor: C.purpleDark },
              }}>
              Voir tous les restaurants
            </Button>
          </Box>
        )}

        {/* ── CTA PARTENAIRE ── */}
        <Box sx={{
          mt: 8, mb: 2, borderRadius: '20px', overflow: 'hidden',
          background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 100%)`,
          p: { xs: 4, md: 5 }, textAlign: 'center',
        }}>
          <Typography sx={{
            color: C.gold, fontWeight: 600, fontSize: '0.75rem',
            letterSpacing: 2, textTransform: 'uppercase', mb: 1.5,
          }}>
            Espace restaurateur
          </Typography>
          <Typography sx={{
            color: 'white', fontWeight: 800,
            fontSize: { xs: '1.5rem', md: '2rem' },
            lineHeight: 1.2, mb: 1.5,
          }}>
            Rejoignez Usafiri
          </Typography>
          <Typography sx={{
            color: 'rgba(255,255,255,0.55)', mb: 3.5, fontSize: '0.9rem', lineHeight: 1.7,
          }}>
            Développez votre activité avec notre réseau à Mayotte.
          </Typography>
          <Button
            component={Link} to="/partner/register"
            variant="contained"
            sx={{
              bgcolor: C.gold, color: C.purpleDark, fontWeight: 800,
              px: 4, py: 1.3, borderRadius: '12px', textTransform: 'none', fontSize: '0.92rem',
              boxShadow: '0 4px 20px rgba(253,185,39,0.4)',
              '&:hover': { bgcolor: '#fdc840' },
            }}
          >
            Devenir Partenaire
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default RestaurantHome;
